/**
 * Data router route tree: loaders, route definitions, and error boundary.
 * SECURITY: Auth and allow checks run in loaders before render.
 * SECURITY (CWE-501): Components get tenant context (customer) from authStore via useStore() hook.
 * Never use URL params, navigation state, or localStorage for tenant scoping of data.
 *
 * Page modules use React Router `lazy` so Vite emits per-route async chunks; the initial
 * graph for bootstrap no longer eagerly pulls every authenticated page.
 */

import React from "react";
import { Navigate, useParams } from "react-router";
import {
  rootLoader,
  indexLoader,
  protectedLayoutLoader,
  deallensViewLoader,
  publicAuthOnlyLoader,
  devOnlySignupLoader,
  createAllowLoader,
} from "./loaders";
import { AuthenticatedLayoutWrapper } from "./wrappers";
import { AUTH_ROLES } from "@/constants/auth";
import {
  canAccessCampaigns,
  canAccessDealLensSharedLink,
  canAccessDeals,
  canAccessOpportunities,
  canAccessProperties,
} from "./roleGateMetadata";
import App from "@/App";
import ErrorPage from "@/error";
const RELOAD_KEY = "vite_chunk_reload";

/** RR7 initial hydration fallback; static `#bootstrap-loading` in index.html covers this phase. */
function RootHydrateFallback() {
  return null;
}

/**
 * Detects stale-chunk / dynamic-import errors that Vite surfaces when HMR
 * invalidates a module or a production deploy changes chunk hashes.
 * @param {Error} error
 * @returns {boolean}
 */
function isChunkLoadError(error) {
  const msg = error?.message ?? "";
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    error?.name === "ChunkLoadError"
  );
}

/**
 * Build RR v6 `lazy` field: dynamic import default export as `Component`.
 * On chunk-load failure, reloads the page **once** per pathname to pick up
 * fresh module URLs.  Uses sessionStorage to prevent infinite reload loops.
 * @param {() => Promise<{ default: import("react").ComponentType }>} loadPage
 * @returns {{ lazy: import("react-router-dom").LazyRouteFunction<import("react-router-dom").RouteObject> }}
 */
function lazyRoute(loadPage) {
  return {
    lazy: () =>
      loadPage().then(
        (m) => ({ Component: m.default }),
        (err) => {
          if (isChunkLoadError(err)) {
            const reloadedPath = sessionStorage.getItem(RELOAD_KEY);
            if (reloadedPath !== window.location.pathname) {
              sessionStorage.setItem(RELOAD_KEY, window.location.pathname);
              window.location.reload();
              return new Promise(() => {});
            }
            sessionStorage.removeItem(RELOAD_KEY);
          }
          throw err;
        }
      ),
  };
}

/**
 * Authenticated routes. Order matters: more specific paths must come before less specific.
 * E.g. "properties/:id" before "properties", "conversation/:id/:a/:b" before "conversation/:id".
 * Components get auth data via useStore() hook directly - no prop injection wrapper needed.
 *
 * Each entry spreads `lazyRoute` so only the matched path loads its page chunk on navigation.
 */
export const AUTHENTICATED_ROUTES = [
  {
    path: "background",
    allow: (a) => a.isSuperAdmin,
    ...lazyRoute(() => import("@/pages/BackgroundRecording")),
  },

  {
    path: "real-estate-agent",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/RealEstateAgent")),
  },
  {
    path: "campaign-builder",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/Playground")),
  },
  { path: "prompts", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/PromptsDashboard")) },

  { path: "case-study", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/CaseStudy")) },
  { path: "customers", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/CustomersDashboard")) },
  { path: "users", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/UsersDashboard")) },
  { path: "contacts", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/ContactsDashboard")) },
  { path: "import", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/ImportModule")) },
  {
    path: "campaigns/:campaignId?",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/CampaignsDashboard")),
  },
  {
    // Properties/:id is deprecated; redirect to /property/:id
    path: "properties/:id",
    allow: canAccessProperties,
    requiredRole: AUTH_ROLES.REALTOR,
    lazy: () =>
      Promise.resolve({
        Component: function RedirectToPropertyDetail() {
          const { id } = useParams();
          return <Navigate to={`/property/${id}`} replace />;
        },
      }),
  },
  {
    path: "properties",
    allow: canAccessProperties,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/PropertyList")),
  },
  {
    path: "property/:id/photos-upload",
    allow: canAccessProperties,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/PropertyPhotoUpload")),
  },
  {
    path: "property/:id/exit-strategy-analysis",
    allow: canAccessProperties,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/ExitStrategyAnalysis")),
  },
  {
    path: "property/:id",
    allow: canAccessProperties,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/PropertyDetail")),
  },
  {
    path: "deallens/:id/:sender_id/:receiver_id",
    allow: canAccessDealLensSharedLink,
    requiredRole: AUTH_ROLES.INVESTOR,
    ...lazyRoute(() => import("@/pages/DealLensDetail")),
  },
  {
    path: "deallens/:id",
    allow: canAccessDeals,
    requiredRole: AUTH_ROLES.INVESTOR,
    ...lazyRoute(() => import("@/pages/DealLensDetail")),
  },
  { path: "intents", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/IntentsDashboard")) },
  {
    path: "scheduled-messages",
    allow: (a) => a.isSuperAdmin,
    ...lazyRoute(() => import("@/pages/ScheduledMessages")),
  },
  { path: "schema-management", allow: (a) => a.isSuperAdmin, ...lazyRoute(() => import("@/pages/SchemaManagement")) },
  { path: "knowledge-base", allow: () => true, ...lazyRoute(() => import("@/pages/KnowledgeBaseDashboard")) },
  { path: "settings", allow: () => true, ...lazyRoute(() => import("@/pages/Settings")) },
  { path: "soon", allow: () => true, ...lazyRoute(() => import("@/pages/ComingSoon")) },
  {
    path: "parcel-search",
    allow: canAccessDeals,
    requiredRole: AUTH_ROLES.INVESTOR,
    ...lazyRoute(() => import("./ParcelSearchRoute")),
  },
  { path: "beta/:poc", allow: () => true, ...lazyRoute(() => import("@/pages/PocWrapper")) },
  {
    path: "conversation/:campaignId/:sender/:receiver",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/ConversationViewer")),
  },
  {
    path: "conversation/:campaignId/:conversationId",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/ConversationViewer")),
  },
  {
    path: "conversation/:campaignId",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/ConversationViewer")),
  },

  {
    path: "opportunities/swipe",
    allow: canAccessOpportunities,
    requiredRole: AUTH_ROLES.INVESTOR,
    ...lazyRoute(() => import("@/pages/Opportunities/OpportunitiesSwipe")),
  },
  {
    path: "opportunities",
    allow: canAccessOpportunities,
    requiredRole: AUTH_ROLES.INVESTOR,
    ...lazyRoute(() => import("@/pages/Opportunities")),
  },
  {
    path: "unified-email-inbox",
    allow: canAccessCampaigns,
    requiredRole: AUTH_ROLES.REALTOR,
    ...lazyRoute(() => import("@/pages/UnifiedEmailInboxTest")),
  },
];

/** Simple map: route config → React Router route object. */
function buildAuthenticatedChildren() {
  return [
    ...AUTHENTICATED_ROUTES.map(({ path, allow = () => true, requiredRole = "", lazy: lazyFn }) => ({
      path,
      loader: createAllowLoader(allow, { requiredRole }),
      lazy: lazyFn,
    })),
    { path: "*", ...lazyRoute(() => import("@/pages/NotFound")) },
  ];
}

/**
 * Full route config for createBrowserRouter.
 * Root has path "/" with public routes (signin, etc.) and a pathless authenticated layout route.
 * The pathless layout route matches any path not matched by public routes, then checks
 * authentication in its loader and renders AuthenticatedLayoutWrapper with child routes.
 * This ensures deep links like /properties/abc123-property-token match correctly.
 */
export function createRoutes() {
  return [
    {
      path: "/",
      loader: rootLoader,
      element: <App />,
      hydrateFallbackElement: <RootHydrateFallback />,
      errorElement: <ErrorPage />,
      children: [
        { index: true, loader: indexLoader, element: <Navigate to="/signin" replace /> },
        { path: "signin", ...lazyRoute(() => import("./SignInRoute")) },
        {
          path: "signup",
          loader: devOnlySignupLoader,
          ...lazyRoute(() => import("@/pages/Authentication/SignUp")),
        },
        {
          path: "confirm-signup",
          loader: publicAuthOnlyLoader,
          ...lazyRoute(() => import("@/pages/Authentication/ConfirmSignUp")),
        },
        {
          path: "confirmation",
          loader: publicAuthOnlyLoader,
          lazy: () =>
            Promise.resolve({
              Component: function RedirectLegacyConfirmation() {
                return <Navigate to="/confirm-signup" replace />;
              },
            }),
        },

        {
          path: "investor-registration",
          ...lazyRoute(() => import("@/pages/InvestorRegistration/InvestorRegistrationIndex")),
        },
        {
          path: "deallens/view",
          loader: deallensViewLoader,
          ...lazyRoute(() => import("@/pages/DealLensView")),
        },
        {
          path: "property/photos/view",
          loader: deallensViewLoader,
          ...lazyRoute(() => import("@/pages/PropertyPhotosView")),
        },
        // Single pathless layout route for all authenticated routes
        // protectedLayoutLoader checks auth; children have their own allow loaders
        {
          loader: protectedLayoutLoader,
          element: <AuthenticatedLayoutWrapper />,
          children: buildAuthenticatedChildren(),
        },
      ],
    },
  ];
}
